import React from "react";
import { showToast } from "services/utils/Status";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Divider,
} from "@mui/material";

export const updateCrossCategoryComponents = (
  changedComponent,
  allComponentsMap,
  totalSalary,
) => {
  const updatedCategories = {};
  let currentComponentsMap = { ...allComponentsMap };

  const getComponentValue = (code) => {
    if (!code) return 0;

    const upperCode = code.toUpperCase();

    if (upperCode === "GROSS") {
      return totalSalary;
    }

    for (const components of Object.values(updatedCategories)) {
      for (const component of components) {
        const componentCode =
          component.code?.toUpperCase() ||
          component.component_name?.code?.toUpperCase();

        if (componentCode === upperCode) {
          return parseFloat(component.amount) || 0;
        }
      }
    }

    for (const components of Object.values(currentComponentsMap)) {
      for (const component of components) {
        const componentCode =
          component.code?.toUpperCase() ||
          component.component_name?.code?.toUpperCase();

        if (componentCode === upperCode) {
          return parseFloat(component.amount) || 0;
        }
      }
    }

    return 0;
  };

  let hasChanges = true;
  let maxIterations = 10;
  let iteration = 0;

  while (hasChanges && iteration < maxIterations) {
    hasChanges = false;
    iteration++;

    for (const [category, components] of Object.entries(currentComponentsMap)) {
      const currentUpdated = updatedCategories[category] || [...components];
      const updatedComponents = [...currentUpdated];
      let categoryChanged = false;

      for (let i = 0; i < updatedComponents.length; i++) {
        const component = updatedComponents[i];
        const componentCode =
          component.code?.toUpperCase() ||
          component.component_name?.code?.toUpperCase();
        const changedCode =
          changedComponent.code?.toUpperCase() ||
          changedComponent.component_name?.code?.toUpperCase();

        if (componentCode === changedCode) {
          continue;
        }

        let newAmount = component.amount || 0;

        if (
          component.computation_method === "Percentage" &&
          component.value_based_on &&
          component.computation_value
        ) {
          const basedOn = Array.isArray(component.value_based_on)
            ? component.value_based_on
            : [component.value_based_on];

          let baseAmount = 0;
          basedOn.forEach((dep) => {
            baseAmount += getComponentValue(dep);
          });

          newAmount =
            (parseFloat(component.computation_value) / 100) * baseAmount;
        }

        if (
          component.computation_method === "Formula" &&
          component.value_based_formula
        ) {
          try {
            let expression = component.value_based_formula
              .toUpperCase()
              .replace(/\s+/g, "");
            const variableValues = {};
            const variableNames = expression.match(/\b[A-Z]+\b/g) || [];
            const uniqueVariables = [...new Set(variableNames)];

            uniqueVariables.forEach((variable) => {
              variableValues[variable] = getComponentValue(variable);
            });

            let evaluatedExpression = expression;
            Object.entries(variableValues).forEach(([key, value]) => {
              const regex = new RegExp(`\\b${key}\\b`, "g");
              evaluatedExpression = evaluatedExpression.replace(
                regex,
                value.toString(),
              );
            });

            const calculate = (expr) => {
              while (expr.includes("(")) {
                expr = expr.replace(/\(([^()]+)\)/g, (match, innerExpr) => {
                  return calculate(innerExpr);
                });
              }

              const mulDivRegex =
                /([+-]?\d*\.?\d+)\s*([*/])\s*([+-]?\d*\.?\d+)/;
              while (mulDivRegex.test(expr)) {
                expr = expr.replace(mulDivRegex, (match, a, op, b) => {
                  const numA = parseFloat(a);
                  const numB = parseFloat(b);
                  return op === "*" ? numA * numB : numA / numB;
                });
              }

              const addSubRegex =
                /([+-]?\d*\.?\d+)\s*([+-])\s*([+-]?\d*\.?\d+)/;
              while (addSubRegex.test(expr)) {
                expr = expr.replace(addSubRegex, (match, a, op, b) => {
                  const numA = parseFloat(a);
                  const numB = parseFloat(b);
                  return op === "+" ? numA + numB : numA - numB;
                });
              }

              return parseFloat(expr) || 0;
            };

            newAmount = calculate(evaluatedExpression);
          } catch (error) {
            showToast("error", "Error calculating formula:", error);
            newAmount = 0;
          }
        }

        if (
          (component.computation_method === "Percentage" ||
            component.computation_method === "Formula") &&
          Math.abs(newAmount - (component.amount || 0)) > 0.01
        ) {
          updatedComponents[i] = {
            ...component,
            amount: Number(newAmount.toFixed(2)),
          };
          categoryChanged = true;
          hasChanges = true;
        }
      }

      if (categoryChanged) {
        updatedCategories[category] = updatedComponents;
        currentComponentsMap[category] = updatedComponents;
      }
    }
  }

  return updatedCategories;
};

export const parsePercentageValue = (str = "") => {
  if (!str || !/\bof\b/i.test(str)) {
    return { computation_value: "", value_based_on: [] };
  }

  const [valuePart, basePart] = str.split(/\bof\b/i);

  return {
    computation_value: valuePart.replace(/%/g, "").trim(),

    value_based_on: basePart
      .split("+")
      .map((v) => v.trim())
      .filter(Boolean),
  };
};

export const buildSpecialAllowanceFormula = (components) => {
  const deductionCodes = components
    .filter(
      (comp) =>
        !comp.isSpecialAllowance &&
        comp.code &&
        comp.amount !== undefined &&
        comp.amount !== null &&
        !Number.isNaN(Number(comp.amount)),
    )
    .map((comp) => comp.code);

  return deductionCodes.length
    ? `GROSS - ${[...deductionCodes].reverse().join(" - ")}`
    : "GROSS";
};

export const updatePercentageDisplay = (component) => {
  if (!component || component.computation_method !== "Percentage") {
    return component;
  }

  const enhanced = { ...component };

  if (enhanced.computation_value && enhanced.value_based_on) {
    const basedOn = Array.isArray(enhanced.value_based_on)
      ? enhanced.value_based_on
      : [enhanced.value_based_on];

    enhanced.value_based_percentage = `${
      enhanced.computation_value
    }% of ${basedOn.join(" + ")}`;
  } else {
    enhanced.value_based_percentage = "";
  }

  return enhanced;
};

export const formatComponents = (components = []) => {
  return components.map((component) => ({
    component_id: component.component_name?.id || component.id,
    component_category: component?.component_category,
    computation_method: component.computation_method,
    component_name: component?.component_name?.label,
    computation_value:
      component?.computation_method === "Fixed Amount"
        ? component?.amount
        : component?.computation_method === "Formula"
          ? component?.value_based_formula
          : component?.value_based_percentage,
    amount: component.amount || 0,
  }));
};

export const SummaryData = ({
  totalCTC,
  totalEmployerContributions,
  totalEarnings,
  totalDeductions,
  netSalary,
  isNetSalaryNegative,
}) => {
  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: "#f8f9fa",
        borderRadius: 2,
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography sx={{ fontWeight: 600 }}>Summary</Typography>

      <TableContainer>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell colSpan={2} sx={{ border: "none", py: 1 }}></TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 800,
                  border: "none",
                  pl: 0,
                  fontSize: "1.2rem",
                  color: "#2e7d32",
                }}
              >
                Total Cost to Company (CTC)
              </TableCell>
              <TableCell
                sx={{
                  border: "none",
                  fontWeight: 800,
                  fontSize: "1.2rem",
                  color: "#2e7d32",
                }}
                align="right"
              >
                {totalCTC.toFixed(2)}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 600, border: "none", pl: 0 }}>
                Employer Contribution
              </TableCell>
              <TableCell sx={{ border: "none" }} align="right">
                {totalEmployerContributions.toFixed(2)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, border: "none", pl: 0 }}>
                Gross Salary
              </TableCell>
              <TableCell sx={{ border: "none" }} align="right">
                {totalEarnings.toFixed(2)}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 600, border: "none", pl: 0 }}>
                Deductions
              </TableCell>
              <TableCell sx={{ border: "none" }} align="right">
                {totalDeductions.toFixed(2)}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell colSpan={2} sx={{ border: "none", py: 1 }}>
                <Divider />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  border: "none",
                  pl: 0,
                  fontSize: "1.1rem",
                }}
              >
                Net Salary
              </TableCell>
              <TableCell
                sx={{
                  border: "none",
                  fontWeight: 700,
                  fontSize: "1.1rem",
                }}
                align="right"
              >
                {netSalary.toFixed(2)}
                {isNetSalaryNegative && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {"Net salary cannot be negative"}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
